import React, { useEffect, useMemo, useState } from 'react';
import './StagingSearchList.css';

const API = '/api/dashboards/admin/search-list';

function token() {
  try {
    const admin = JSON.parse(localStorage.getItem('admin') || 'null');
    return admin?.accessToken || admin?.token;
  } catch {
    return null;
  }
}

function normalise(value) {
  return String(value || '').trim().toLowerCase().replace(/\s+/g, ' ');
}

function dateKey(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/London',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).format(date);
}

function todayUK() {
  return dateKey(new Date());
}

function dateLabel(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('en-GB', {
    timeZone: 'Europe/London',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

function csvCell(value) {
  let text = String(value ?? '');
  if (/^[=+\-@\t\r]/.test(text)) text = "'" + text;
  return '"' + text.replace(/"/g, '""') + '"';
}

function startOfPeriod(period) {
  const today = todayUK();
  const date = new Date(today + 'T12:00:00Z');

  if (period === 'all') return '';
  if (period === 'today') return today;
  if (period === 'yesterday') date.setUTCDate(date.getUTCDate() - 1);
  if (period === '7') date.setUTCDate(date.getUTCDate() - 6);
  if (period === '30') date.setUTCDate(date.getUTCDate() - 29);
  if (period === 'month') date.setUTCDate(1);
  if (period === 'lastmonth') {
    date.setUTCDate(1);
    date.setUTCMonth(date.getUTCMonth() - 1);
  }
  if (period === 'year') {
    date.setUTCMonth(0);
    date.setUTCDate(1);
  }
  return date.toISOString().slice(0, 10);
}

function endOfPeriod(period) {
  if (period !== 'lastmonth') return todayUK();
  const date = new Date(todayUK() + 'T12:00:00Z');
  date.setUTCDate(0);
  return date.toISOString().slice(0, 10);
}

export default function StagingSearchList() {
  const [searches, setSearches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [period, setPeriod] = useState('30');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [term, setTerm] = useState('');
  const [category, setCategory] = useState('');
  const [screen, setScreen] = useState('');
  const [sort, setSort] = useState('newest');
  const [exactTerm, setExactTerm] = useState('');
  const [refresh, setRefresh] = useState(0);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError('');

      try {
        const accessToken = token();
        if (!accessToken) throw new Error('Administrator session required.');

        const all = [];
        let total = null;

        for (let page = 1; page <= 1000; page++) {
          const response = await fetch(`${API}?page=${page}&perPage=200`, {
            headers: {
              'x-access-token': accessToken,
              Accept: 'application/json'
            },
            cache: 'no-store'
          });

          const data = await response.json();
          if (!response.ok || !data.success || !Array.isArray(data.searches)) {
            throw new Error(data.message || 'Unable to load searches.');
          }

          all.push(...data.searches);
          total = Number(data.total);

          if (all.length >= total || !data.searches.length) break;
          if (page === 1000) throw new Error('Search history exceeds the reporting limit.');
        }

        if (!cancelled) setSearches(all);
      } catch (err) {
        if (!cancelled) setError(err.message || 'Unable to load searches.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => { cancelled = true; };
  }, [refresh]);

  const parsed = useMemo(() => searches.map(item => {
    const raw = String(item.term || '').trim();
    const separator = raw.indexOf(' > ');
    return {
      ...item,
      query: separator >= 0 ? raw.slice(separator + 3).trim() : raw,
      category: separator >= 0 ? raw.slice(0, separator).trim() : '',
      screen: separator >= 0 ? 'Category' : 'Home',
      date: dateKey(item.submittedAt)
    };
  }), [searches]);

  const categories = useMemo(
    () => ['Brows and lashes', 'Hair', 'Hair removal', 'Make up', 'Nails', 'Skin'].sort(),
    [parsed]
  );

  const rows = useMemo(() => {
    const start = period === 'custom' ? from : startOfPeriod(period);
    const end = period === 'custom' ? to : endOfPeriod(period);
    const q = normalise(term);

    return parsed.filter(row =>
      (!start || row.date >= start) &&
      (!end || row.date <= end) &&
      (!q || normalise(row.query).includes(q)) &&
      (!exactTerm || normalise(row.query) === exactTerm) &&
      (!category || row.category === category) &&
      (!screen || row.screen === screen)
    ).sort((a, b) => {
      const delta = new Date(a.submittedAt).getTime() -
        new Date(b.submittedAt).getTime();
      return sort === 'oldest' ? delta : -delta;
    });
  }, [parsed, period, from, to, term, exactTerm, category, screen, sort]);

  const insights = useMemo(() => {
    const map = new Map();
    rows.forEach(row => {
      const key = normalise(row.query);
      if (!key) return;
      if (!map.has(key)) map.set(key, {
        term: row.query,
        count: 0,
        clients: new Set()
      });
      const entry = map.get(key);
      entry.count++;
      if (normalise(row.clientName)) entry.clients.add(normalise(row.clientName));
    });
    return [...map.values()].sort((a, b) =>
      b.count - a.count || a.term.localeCompare(b.term)
    );
  }, [rows]);

  const clients = new Set(
    rows.map(row => normalise(row.clientName)).filter(Boolean)
  ).size;

  function exportCSV() {
    const data = [
      ['Search term', 'Category', 'Client name', 'Submitted (UK time)'],
      ...rows.map(row => [
        row.query, row.category, row.clientName, dateLabel(row.submittedAt)
      ])
    ];

    const csv = data.map(line => line.map(csvCell).join(',')).join('\r\n');
    const url = URL.createObjectURL(new Blob([csv], {
      type: 'text/csv;charset=utf-8'
    }));
    const a = document.createElement('a');
    a.href = url;
    a.download = 'Beauty_Spot_Search_List.csv';
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="bss-search-page">
      <div className="bss-heading">
        <div>
          <h1>Search list</h1>
          <p>Terms clients searched on Home and category screens, including provider names and locations.</p>
        </div>
        <button onClick={exportCSV} disabled={loading || !!error}>
          Export filtered searches ↗
        </button>
      </div>

      <div className="bss-period">
        <span>Current date: {todayUK()} · Europe/London</span>
        <label>Reporting period
          <select value={period} onChange={e => setPeriod(e.target.value)}>
            <option value="today">Today</option>
            <option value="yesterday">Yesterday</option>
            <option value="7">Last 7 days</option>
            <option value="30">Last 30 days</option>
            <option value="month">This month</option>
            <option value="lastmonth">Last month</option>
            <option value="year">This year</option>
            <option value="all">All time</option>
            <option value="custom">Custom dates</option>
          </select>
        </label>
      </div>

      {period === 'custom' && (
        <div className="bss-filters">
          <label>From<input type="date" max={todayUK()} value={from}
            onChange={e => setFrom(e.target.value)} /></label>
          <label>To<input type="date" max={todayUK()} value={to}
            onChange={e => setTo(e.target.value)} /></label>
          {from && to && from > to && <p>Start date must precede end date.</p>}
        </div>
      )}

      {error && <div className="bss-error" role="alert">
        {error} <button onClick={() => setRefresh(v => v + 1)}>Retry</button>
      </div>}

      {loading ? <p>Loading search records…</p> : !error && <>
        <div className="bss-summary">
          <div><small>Searches submitted</small><strong>{rows.length}</strong>
            <span>Repeat submissions included</span></div>
          <div><small>Clients searching</small><strong>{clients}</strong>
            <span>Distinct recorded client names</span></div>
          <div><small>Different search terms</small><strong>{insights.length}</strong>
            <span>Ignoring case and extra spaces</span></div>
        </div>

        <p className="bss-period-caption">
          {period === 'all' ? 'All available dates' :
            `${period === 'custom' ? (from || 'Start') : startOfPeriod(period)} – ${period === 'custom' ? (to || 'End') : endOfPeriod(period)}`}
          {' · All figures follow the selected filters.'}
        </p>
        <div className="bss-filter-card">
          <div className="bss-filters">
            <label>Search term<input value={term}
              placeholder="e.g. braids, nails or facial"
              onChange={e => { setTerm(e.target.value); setExactTerm(''); }} /></label>
            <label>Category<select value={category}
              onChange={e => setCategory(e.target.value)}>
              <option value="">All categories</option>
              {categories.map(value => <option key={value} value={value}>{value}</option>)}
            </select></label>
            <label>Search screen<select value={screen}
              onChange={e => setScreen(e.target.value)}>
              <option value="">All screens</option>
              <option value="Home">Home</option>
              <option value="Category">Category</option>
            </select></label>
            <label>Sort by<select value={sort} onChange={e => setSort(e.target.value)}>
              <option value="newest">Newest first</option>
              <option value="oldest">Oldest first</option>
            </select></label>
            <button className="bss-reset" onClick={() => {
              setTerm(''); setCategory(''); setScreen(''); setSort('newest'); setExactTerm('');
            }}>Reset filters</button>
          </div>
          {exactTerm && <div className="bss-exact-row">
            <span>Exact term: <strong>{exactTerm}</strong></span>
            <button onClick={() => setExactTerm('')}>Clear ×</button>
          </div>}
        </div>

        <section className="bss-panel">
          <h2>Most searched terms</h2>
          <p>Includes provider names and locations typed into search.</p>
          <div className="bss-table-wrap">
            <table>
              <thead><tr><th>Search term</th><th>Searches</th><th>Clients</th></tr></thead>
              <tbody>
                {insights.slice(0, 8).map(item => (
                  <tr key={normalise(item.term)}>
                    <td><button className="bss-link" onClick={() => {
                      setTerm('');
                      setExactTerm(normalise(item.term));
                    }}>{item.term} ↗</button></td>
                    <td>{item.count}</td><td>{item.clients.size}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {!insights.length && <p>No search terms in this view.</p>}
          </div>
        </section>

        <section className="bss-panel">
          <h2>Search log</h2>
          <p>Each row records a saved search term.</p>
          <p>{rows.length} search submissions shown</p>
          <div className="bss-table-wrap">
            <table>
              <thead><tr>
                <th>Term searched</th><th>Screen</th><th>Category</th>
                <th>Client</th><th>Submitted</th>
              </tr></thead>
              <tbody>
                {rows.map(row => (
                  <tr key={row.id}>
                    <td>{row.query || '—'}</td>
                    <td>{row.screen}</td>
                    <td>{row.category || 'All categories'}</td>
                    <td>{row.clientName || 'Unknown client'}</td>
                    <td>{dateLabel(row.submittedAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {!rows.length && <p>No searches match the selected filters.</p>}
          </div>
        </section>
      </>}
    </div>
  );
}
